import {
  DESIGN_ENUMS,
  DESIGN_INLINE,
  DESIGN_INLINE_VERTICAL,
  DESIGN_STANDARD,
  DESIGN_VERTICAL,
  SIZE_ENUMS,
  SIZE_STANDARD,
} from '../../ChannelList/constants/carousels';
import { INDENT_FOR_LW_IN_POPUP } from '../constants';
import { TYPE_DEFAULT, TYPE_ENUMS } from '../constants/channel';
import { DEEP_SEARCH_REDIRECT_ENUMS } from '../constants/deepSearch';
import { ENVIRONMENT_ENUMS } from '../constants/environment';
import { GEN_AI_MAX_VIDEO_COUNT, GEN_AI_MIN_VIDEO_COUNT } from '../constants/genAi';
import { AVAILABLE_CHIP_TAGS, AVAILABLE_DEEP_SEARCH_TAGS } from '../constants/label';
import { LAYOUT_TYPE_ENUMS, LAYOUT_TYPE_IN_POPUP } from '../constants/lw';
import { ALL_SOCIAL_ENGAGEMENT_TYPES, SHARES, VIDEO_ENGAGEMENT_SOCIAL_TYPE } from '../constants/videoEngagement';
import { supportedLanguages } from '@/modules/common/constants/languages';
import { APPEARANCE_ENUMS } from '@/mui/constants';

import { RawWatchType, WatchType } from '@/modules/common/types';
import { ChannelType } from '@/modules/common/types/channel';

import { initialState as initial } from '../redux/slices/common';
import { processConfiguration } from '@/modules/common/helpers/configuration';

import { isInternalType } from './embedCodeParams';
import { getNumberInRange } from './number';
import { replaceLogoDomain } from './url';
import { isMobileUserAgent } from './userAgent';

export function toValidEnum<T extends string>(enumValue: unknown, enumsList: readonly T[], defaultEnum: T): T {
  return enumsList.includes(enumValue as T) ? (enumValue as T) : defaultEnum;
}

const toInt = (num: string | number | undefined | boolean, defaultValue: number) => {
  if (typeof num === 'number') {
    return num;
  }

  if (typeof num === 'boolean' || num === undefined || num === null) {
    return defaultValue;
  }

  const parsed = parseInt(num, 10);

  return Number.isNaN(parsed) ? defaultValue : parsed;
};

const getChannelModel = (chl: Partial<ChannelType>): ChannelType => ({
  bg: chl.bg ?? '',
  bgLogo: chl.bgLogo ?? '',
  cTitleColor: chl.cTitleColor ?? '',
  cTitleSize: chl.cTitleSize ?? '',
  cVidInfoColor: chl.cVidInfoColor ?? '',
  cVidInfoSize: chl.cVidInfoSize ?? '',
  cVidTitleColor: chl.cVidTitleColor ?? '',
  cVidTitleSize: chl.cVidTitleSize ?? '',
  descColor: chl.descColor ?? '',
  description: chl.description ?? '',
  status: chl.status ?? 1,
  userId: chl.userId ?? 0,
  design: toValidEnum(chl.design, DESIGN_ENUMS, DESIGN_STANDARD),
  size: toValidEnum(chl.size, SIZE_ENUMS, SIZE_STANDARD),
  id: chl.id ?? -1,
  logo: replaceLogoDomain(chl.logo),
  name: chl.name ?? '',
  playlistId: chl.playlistId ?? -1,
  title: chl.title ?? '',
  titleColor: chl.titleColor ?? '',
  type: toValidEnum(chl.type, TYPE_ENUMS, TYPE_DEFAULT),
  vidInfoColor: chl.vidInfoColor ?? '',
  vidTitleColor: chl.vidTitleColor ?? '',
  ...(isInternalType
    ? {
        bg: '',
        bgLogo: '',
        cTitleColor: '',
        cTitleSize: '',
        cVidInfoColor: '',
        cVidInfoSize: '',
        cVidTitleColor: '',
        cVidTitleSize: '',
        descColor: '',
        titleColor: '',
        vidInfoColor: '',
        vidTitleColor: '',
      }
    : {}),
  disabled: false,
  playlistLoaded: false,
  playlist: [],
  playerPropsPassThrow: {},
});

export const getWatchModel = (conf: RawWatchType): WatchType => {
  const search = conf.search ?? initial.search;
  const deepSearch = conf.deepSearch ?? initial.deepSearch;
  const emailShare = conf.emailShare ?? initial.emailShare;

  let replaceInlineToStandard = isMobileUserAgent;
  const channels = (conf.channels || []).map((chl) => {
    const channel = getChannelModel(chl);

    if (channel.design === DESIGN_INLINE) {
      if (replaceInlineToStandard) {
        channel.design = DESIGN_STANDARD;
      } else {
        replaceInlineToStandard = true;
      }
    }

    return channel;
  });

  const verticalTypeContains = channels.some((channel) =>
    [DESIGN_VERTICAL, DESIGN_INLINE_VERTICAL].includes(channel.design),
  );

  const layout = !verticalTypeContains
    ? toValidEnum(conf.layout, LAYOUT_TYPE_ENUMS, initial.layout)
    : LAYOUT_TYPE_IN_POPUP;

  const hubOffsetDelta = layout === LAYOUT_TYPE_IN_POPUP && isMobileUserAgent ? INDENT_FOR_LW_IN_POPUP : 0;

  const dsTags = (conf.dsTags || []).filter((tag) => AVAILABLE_DEEP_SEARCH_TAGS.includes(tag));
  const delayPreview = toInt(conf.delayPreview, initial.delayPreview);
  const tags = conf.tags ?? initial.tags;

  let configuration;

  if (conf.configuration) {
    try {
      configuration = JSON.parse(conf.configuration);
    } catch {
      /* empty */
    }
  }

  const language =
    !isInternalType && typeof conf.language === 'string' && supportedLanguages.includes(conf.language)
      ? conf.language
      : initial.language;

  return {
    appearance: toValidEnum(conf.appearance, APPEARANCE_ENUMS, initial.appearance),
    autoplay: conf.autoplay ?? initial.autoplay,
    autoplayMobile: conf.autoplayMobile ?? initial.autoplayMobile,
    autoplayNext: conf.autoplayNext ?? initial.autoplayNext,
    backMobile: conf.backMobile ?? initial.backMobile,
    basePath: conf.basePath?.replace('https://', '') ?? '',
    bg: conf.bg ?? initial.bg,
    cDescColor: conf.cDescColor ?? initial.cDescColor,
    cDescSize: conf.cDescSize ?? initial.cDescSize,
    cInfoColor: conf.cInfoColor ?? initial.cInfoColor,
    cTitleColor: conf.cTitleColor ?? initial.cTitleColor,
    cTitleSize: conf.cTitleSize ?? initial.cTitleSize,
    cVidTitleColor: conf.cVidTitleColor ?? initial.cVidTitleColor,
    cVidTitleSize: conf.cVidTitleSize ?? initial.cVidTitleSize,
    chapters: conf.chapters ?? initial.chapters,
    color: conf.color ?? initial.color,
    comments: conf.comments ?? initial.comments,
    css: conf.css ?? initial.css,
    dblClickFlow: conf.dblClickFlow ?? initial.dblClickFlow,
    deepSearch: deepSearch && !!dsTags.length,
    delayPreview,
    description: conf.description ?? initial.description,
    dockMobile: conf.dockMobile ?? initial.dockMobile,
    download: conf.download ?? initial.download,
    dsRedirect: toValidEnum(deepSearch && conf.dsRedirect, DEEP_SEARCH_REDIRECT_ENUMS, initial.dsRedirect),
    dsTags,
    emailShare,
    engagement: ALL_SOCIAL_ENGAGEMENT_TYPES.filter(
      (type) =>
        (conf.engagement || []).some(
          (key: string) =>
            Object.prototype.hasOwnProperty.call(VIDEO_ENGAGEMENT_SOCIAL_TYPE, key) &&
            VIDEO_ENGAGEMENT_SOCIAL_TYPE[key as keyof typeof VIDEO_ENGAGEMENT_SOCIAL_TYPE] === type,
        ) &&
        (emailShare || (!emailShare && type !== SHARES)),
    ),
    environment: toValidEnum(conf.environment, ENVIRONMENT_ENUMS, initial.environment),
    excludedTags: (tags && conf.excludedTags) || initial.excludedTags,
    fixedTags: (tags && conf.fixedTags) || initial.fixedTags,
    // empty string is device dependent that why used || and not ?? operator
    font: conf.font || initial.font,
    fontSize: conf.fontSize ?? initial.fontSize,
    genAI: search && (conf.genAI ?? initial.genAI),
    genAICount: getNumberInRange(
      toInt(conf.genAICount, initial.genAICount),
      GEN_AI_MIN_VIDEO_COUNT,
      GEN_AI_MAX_VIDEO_COUNT,
    ),
    highlights: conf.highlights ?? initial.highlights,
    id: conf.id ?? -1,
    iframeRouter: conf.iframeRouter ?? initial.iframeRouter,
    inlinePlayer: { name: conf.inlinePlayer?.name ?? initial.inlinePlayer.name },
    inlineVerticalPlayer: { name: conf.inlineVerticalPlayer?.name ?? initial.inlineVerticalPlayer.name },
    js: conf.js ?? initial.js,
    language,
    languages: conf.languages ?? initial.languages,
    layout,
    offsetBottom: toInt(conf.offsetBottom, initial.offsetBottom),
    offsetTop: toInt(conf.offsetTop, initial.offsetTop) + hubOffsetDelta,
    player: { name: conf.player?.name ?? initial.player.name },
    popupSize: toInt(conf.popupSize, initial.popupSize),
    publisher: { slug: conf.publisher?.slug ?? initial.publisher.slug },
    sTitleColor: conf.sTitleColor ?? initial.sTitleColor,
    sTitleSize: conf.sTitleSize ?? initial.sTitleSize,
    sVidInfoColor: conf.sVidInfoColor ?? initial.sVidInfoColor,
    sVidInfoSize: conf.sVidInfoSize ?? initial.sVidInfoSize,
    sVidTitleColor: conf.sVidTitleColor ?? initial.sVidTitleColor,
    sVidTitleSize: conf.sVidTitleSize ?? initial.sVidTitleSize,
    search,
    searchLabel: conf.searchLabel ?? initial.searchLabel,
    searchPlaceholder: conf.searchPlaceholder ?? initial.searchPlaceholder,
    shareService: conf.shareService ?? initial.shareService,
    slides: conf.slides ?? initial.slides,
    socialShare: conf.socialShare ?? initial.socialShare,
    sound: conf.sound ?? initial.sound,
    soundMobile: conf.soundMobile ?? initial.soundMobile,
    status: conf.status ?? initial.status,
    tabOrder: conf.tabOrder ?? initial.tabOrder,
    tagCount: toInt(!!tags && conf.tagCount, initial.tagCount),
    tagMax: toInt(!!tags && conf.tagMax, initial.tagMax),
    tagMin: toInt(!!tags && conf.tagMin, initial.tagMin),
    tagPages: (tags && conf.tagPages) || [],
    tagTypes: (tags && conf.tagTypes?.filter((tag) => AVAILABLE_CHIP_TAGS.includes(tag))) || [],
    tags,
    title: conf.title ?? initial.title,
    rtl: (!isInternalType && conf.rtl) ?? initial.rtl,
    transcriptDownload: conf.transcriptDownload ?? initial.transcriptDownload,
    verticalPlayer: { name: conf.verticalPlayer?.name ?? initial.verticalPlayer.name },
    videoPreview: conf.videoPreview ?? initial.videoPreview,
    widgetOrigin: conf.widgetOrigin?.replace(/\s|((http|https):)|\//g, '') || initial.widgetOrigin,
    widgetPath: conf.widgetPath ?? initial.widgetPath,
    configuration: processConfiguration(configuration || {}, language),
    ...(isInternalType
      ? {
          appearance: initial.appearance,
          bg: initial.bg,
          cDescColor: initial.cDescColor,
          cDescSize: initial.cDescSize,
          cInfoColor: initial.cInfoColor,
          cTitleColor: initial.cTitleColor,
          cTitleSize: initial.cTitleSize,
          cVidTitleColor: initial.cVidTitleColor,
          cVidTitleSize: initial.cVidTitleSize,
          color: initial.color,
          css: initial.css,
          font: initial.font,
          fontSize: initial.fontSize,
          js: initial.js,
          offsetBottom: initial.offsetBottom,
          offsetTop: initial.offsetTop,
          sTitleColor: initial.sTitleColor,
          sTitleSize: initial.sTitleSize,
          sVidInfoColor: initial.sVidInfoColor,
          sVidInfoSize: initial.sVidInfoSize,
          sVidTitleColor: initial.sVidTitleColor,
          sVidTitleSize: initial.sVidTitleSize,
          configuration: initial.configuration,
        }
      : {}),
    channels,
  };
};
